import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface Blog {
  id: string;
  title: string;
  slug: string;
  short_description: string;
  content: string;
  image_url?: string;
  author_name: string;
  is_published: boolean;
  is_featured: boolean;
  seo_title?: string;
  seo_description?: string;
  seo_keywords?: string;
  reading_time_minutes: number;
  created_at: string;
  updated_at: string;
  category_id?: string;
  blog_categories?: {
    name: string;
    color: string;
    slug: string;
  };
}

interface BlogCategory {
  id: string;
  name: string;
  slug: string;
  color: string;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const useBlogData = () => {
  return useQuery({
    queryKey: ["blogs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blogs")
        .select(`
          *,
          blog_categories (
            name,
            color,
            slug
          )
        `)
        .eq("is_published", true)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Blog[];
    },
  });
};

export const useFeaturedBlogs = () => {
  return useQuery({
    queryKey: ["featured-blogs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blogs")
        .select(`
          *,
          blog_categories (
            name,
            color,
            slug
          )
        `)
        .eq("is_published", true)
        .eq("is_featured", true)
        .order("created_at", { ascending: false })
        .limit(3);

      if (error) throw error;
      return data as Blog[];
    },
  });
};

export const useBlogBySlug = (slug: string) => {
  return useQuery({
    queryKey: ["blog", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blogs")
        .select(`
          *,
          blog_categories (
            name,
            color,
            slug
          )
        `)
        .eq("slug", slug)
        .eq("is_published", true)
        .single();

      if (error) throw error;
      return data as Blog;
    },
    enabled: !!slug,
  });
};

export const useBlogCategories = () => {
  return useQuery({
    queryKey: ["blog-categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_categories")
        .select("*")
        .eq("is_active", true)
        .order("display_order", { ascending: true });

      if (error) throw error;
      return data as BlogCategory[];
    },
  });
};

export const useBlogsByCategory = (categoryId?: string) => {
  return useQuery({
    queryKey: ["blogs-by-category", categoryId],
    queryFn: async () => {
      const query = supabase
        .from("blogs")
        .select(`
          *,
          blog_categories (
            name,
            color,
            slug
          )
        `)
        .eq("is_published", true);

      if (categoryId) {
        query.eq("category_id", categoryId);
      }

      const { data, error } = await query.order("created_at", { ascending: false });

      if (error) throw error;
      return data as Blog[];
    },
    enabled: categoryId !== undefined,
  });
};